import React, { useState, useEffect } from 'react';
import {
  Grid,
  Button,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  Checkbox,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import supabase from '../../supabaseClient';
import { exportData } from '../../utils/dataExport';
import { shiftOptions } from '../../constants/shiftData';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
  padding: theme.spacing(3),
  },
  '&:last-child': {
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: theme.spacing(2.5),
    },
    [theme.breakpoints.up('md')]: {
    paddingBottom: theme.spacing(3),
    },
  },
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
  marginBottom: theme.spacing(1.5),
  },
  '& .MuiFormControlLabel-label': {
    fontSize: { xs: '0.875rem', sm: '0.95rem' },
  },
}));

const ExportButtonContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
  padding: theme.spacing(4),
  },
  marginTop: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
  marginTop: theme.spacing(3),
  },
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.palette.background.paper,
}));

const ExportButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.25, 3),
  [theme.breakpoints.up('sm')]: {
  padding: theme.spacing(1.5, 4),
  },
  fontSize: { xs: '0.875rem', sm: '1rem' },
  fontWeight: 600,
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(52, 152, 219, 0.4)',
  },
  width: { xs: '100%', sm: 'auto' },
}));

const RadioOptionWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
}));

const CheckboxListContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginLeft: '9px', // Align with radio button center (radio button is ~18px wide, centered at 9px)
  paddingLeft: theme.spacing(2.5),
  borderLeft: `2px solid ${theme.palette.divider}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-2px',
    top: '-8px',
    width: '2px',
    height: '8px',
    backgroundColor: theme.palette.divider,
  },
}));

const CheckboxListLabel = styled(Typography)(({ theme }) => ({
  fontSize: { xs: '0.8rem', sm: '0.9rem' },
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
  marginBottom: theme.spacing(1.5),
  },
  fontWeight: 500,
}));

const CheckboxItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(0.75),
  [theme.breakpoints.up('sm')]: {
  marginBottom: theme.spacing(1),
  },
  '& .MuiFormControlLabel-label': {
    fontSize: { xs: '0.875rem', sm: '0.95rem' },
  },
}));


function DataExportForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [exportType, setExportType] = useState('teachers');
  const [recipientType, setRecipientType] = useState('dugsi');
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchClasses = async () => {
      const { data, error } = await supabase
        .from('classes')
        .select('id, name')
        .order('name');
      
      if (!error && data) {
        setClasses(data);
      }
    };

    fetchClasses();
  }, []);

  const handleExport = async () => {
    try {
      setExporting(true);
      
      // Validate required fields based on recipient type
      if (recipientType === 'shift' && selectedShifts.length === 0) {
        throw new Error('Please select at least one shift');
      }
      if (recipientType === 'class' && selectedClasses.length === 0) {
        throw new Error('Please select at least one class');
      }

      const result = await exportData(
        exportType,
        recipientType,
        selectedShifts,
        selectedClasses,
        classes
      );

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Successfully exported ${result.recordCount} records to ${result.filename}`,
          severity: 'success'
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to export data',
        severity: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Export Type Selection */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <StyledCardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, mb: { xs: 0.75, sm: 1 } }}>
                <FileDownloadIcon sx={{ 
                  color: '#3498db', 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' } 
                }} />
                <Typography 
                  variant="h6" 
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  Export Type
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                }}
              >
                Select the type of data to export
              </Typography>
              
              <FormControl component="fieldset" fullWidth>
                <StyledRadioGroup
                  value={exportType}
                  onChange={(e) => setExportType(e.target.value)}
                >
                  <StyledFormControlLabel
                    value="teachers"
                    control={<Radio />}
                    label="Teachers"
                  />
                  <StyledFormControlLabel
                    value="parents"
                    control={<Radio />}
                    label="Parents / Guardians"
                  />
                  <StyledFormControlLabel
                    value="combined"
                    control={<Radio />}
                    label="Combined (All Users)"
                  />
                </StyledRadioGroup>
              </FormControl>
            </StyledCardContent>
          </StyledCard>
        </Grid>

        {/* Export Scope Selection */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <StyledCardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, mb: { xs: 0.75, sm: 1 } }}>
                <FilterListIcon sx={{ 
                  color: '#3498db', 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' } 
                }} />
                <Typography 
                  variant="h6" 
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  Export Scope
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                }}
              >
                Choose which data to include
              </Typography>
              
              <FormControl component="fieldset" fullWidth>
                <StyledRadioGroup
                  value={recipientType}
                  onChange={(e) => {
                    setRecipientType(e.target.value);
                    // Reset selections when changing scope
                    if (e.target.value !== 'shift') setSelectedShifts([]);
                    if (e.target.value !== 'class') setSelectedClasses([]);
                  }}
                >
                  <StyledFormControlLabel
                    value="dugsi"
                    control={<Radio />}
                    label="Entire School"
                  />
                  <RadioOptionWrapper>
                    <StyledFormControlLabel
                      value="shift"
                      control={<Radio />}
                      label="Specific Shift(s)"
                    />
                  </RadioOptionWrapper>
                  <RadioOptionWrapper>
                    <StyledFormControlLabel
                      value="class"
                      control={<Radio />}
                      label="Specific Class(es)"
                    />
                  </RadioOptionWrapper>
                </StyledRadioGroup>
              </FormControl>

              {/* Conditional Selectors */}
              {recipientType === 'shift' && (
                <CheckboxListContainer>
                  <CheckboxListLabel>Select shifts:</CheckboxListLabel>
                  <Box>
                    {shiftOptions.map((shift) => (
                      <CheckboxItem key={shift}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedShifts.indexOf(shift) > -1}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedShifts([...selectedShifts, shift]);
                                } else {
                                  setSelectedShifts(selectedShifts.filter(s => s !== shift));
                                }
                              }}
                            />
                          }
                          label={shift}
                        />
                      </CheckboxItem>
                    ))}
                  </Box>
                </CheckboxListContainer>
              )}

              {recipientType === 'class' && (
                <CheckboxListContainer>
                  <CheckboxListLabel>Select classes:</CheckboxListLabel>
                  <Box>
                    {classes.map((cls) => (
                      <CheckboxItem key={cls.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedClasses.indexOf(cls.id) > -1}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedClasses([...selectedClasses, cls.id]);
                                } else {
                                  setSelectedClasses(selectedClasses.filter(id => id !== cls.id));
                                }
                              }}
                            />
                          }
                          label={cls.name}
                        />
                      </CheckboxItem>
                    ))}
                  </Box>
                </CheckboxListContainer>
              )}
            </StyledCardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Export Button */}
      <ExportButtonContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ExportButton
            variant="contained"
            color="primary"
            size="large"
            onClick={handleExport}
            disabled={exporting || 
              (recipientType === 'shift' && selectedShifts.length === 0) ||
              (recipientType === 'class' && selectedClasses.length === 0)
            }
            startIcon={exporting ? <CircularProgress size={isMobile ? 18 : 20} color="inherit" /> : <DownloadIcon />}
          >
            {exporting ? 'Exporting...' : 'Export as CSV'}
          </ExportButton>
        </Box>
      </ExportButtonContainer>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DataExportForm;
